import React from 'react';
import { Submission, Task } from '../../../types';
import {
  SignificationResponse,
  TensionResponse,
  TriadResponse,
  StonesResponse,
} from '../../../types/signification.types';

interface SignificationMediaDisplayProps {
  submission: Submission;
  task?: Task;
  compact?: boolean;
}

// ── Tension mini preview ──
const TensionPreview: React.FC<{ response: TensionResponse; poles: { label: string }[] }> = ({ response, poles }) => {
  const pct = Math.round(response.weight * 100);
  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide">
        <span style={{ color: 'var(--text-muted, #6B6560)' }}>{poles[0]?.label || 'A'}</span>
        <span style={{ color: 'var(--text-muted, #6B6560)' }}>{poles[1]?.label || 'B'}</span>
      </div>
      <div className="relative h-2 rounded-full" style={{ background: 'var(--app-border, #E8E4DF)' }}>
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2"
          style={{
            left: `${pct}%`,
            transform: `translate(-50%, -50%)`,
            background: 'var(--action, #6E7A5A)',
            borderColor: 'white',
          }}
        />
      </div>
      <div className="text-center text-[11px] font-medium" style={{ color: 'var(--text-faint, #A09890)' }}>
        {pct}% toward {poles[1]?.label || 'B'}
      </div>
    </div>
  );
};

// ── Triad mini preview ──
const TriadPreview: React.FC<{ response: TriadResponse; poles: { label: string; color?: string }[] }> = ({ response, poles }) => {
  const [w0, w1, w2] = response.weights;
  // Simple equilateral triangle in a 80x70 box
  const cx = 40, topY = 6, botY = 64;
  const v0 = { x: cx, y: topY };                       // top
  const v1 = { x: cx - 34, y: botY };                  // bottom-left
  const v2 = { x: cx + 34, y: botY };                  // bottom-right
  const dotX = w0 * v0.x + w1 * v1.x + w2 * v2.x;
  const dotY = w0 * v0.y + w1 * v1.y + w2 * v2.y;

  return (
    <div className="flex flex-col items-center gap-1.5 w-full">
      <svg width="80" height="70" viewBox="0 0 80 70">
        <polygon
          points={`${v0.x},${v0.y} ${v1.x},${v1.y} ${v2.x},${v2.y}`}
          fill="none"
          stroke="var(--app-border, #E8E4DF)"
          strokeWidth="1.5"
        />
        <circle cx={dotX} cy={dotY} r="4" fill="var(--action, #6E7A5A)" />
      </svg>
      <div className="flex items-center gap-2 flex-wrap justify-center">
        {poles.map((p, i) => (
          <span key={i} className="text-[9px] font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted, #6B6560)' }}>
            {p.label} {Math.round((response.weights[i] || 0) * 100)}%
          </span>
        ))}
      </div>
    </div>
  );
};

// ── Stones mini preview ──
const StonesPreview: React.FC<{ response: StonesResponse; poles: { label: string; color?: string }[] }> = ({ response, poles }) => {
  const poleMap = new Map(poles.map(p => [p.label, p]));
  return (
    <div className="flex flex-col items-center gap-1.5 w-full">
      <svg width="80" height="70" viewBox="0 0 100 80">
        <rect x="2" y="2" width="96" height="76" rx="6" fill="none" stroke="var(--app-border, #E8E4DF)" strokeWidth="1" strokeDasharray="4 3" />
        {response.placements.map((p, i) => {
          const pole = poles.find(pl => pl.id === p.stoneId);
          return (
            <g key={i}>
              <circle
                cx={p.x * 96 + 2}
                cy={p.y * 76 + 2}
                r="6"
                fill="var(--text-faint, #A09890)"
                fillOpacity="0.3"
                stroke="var(--text-muted, #6B6560)"
                strokeWidth="1"
              />
              <text
                x={p.x * 96 + 2}
                y={p.y * 76 + 2 + 14}
                textAnchor="middle"
                fontSize="7"
                fontWeight="600"
                fill="var(--text-muted, #6B6560)"
              >
                {(pole?.label || p.stoneId).slice(0, 6)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// ── Main component ──
export const SignificationMediaDisplay: React.FC<SignificationMediaDisplayProps> = ({
  submission,
  task,
  compact = false,
}) => {
  const sigData = submission.significationData;
  if (!sigData || !sigData.responses || sigData.responses.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center" style={{ background: 'var(--app-bg, #F5F2EE)' }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--text-faint, #A09890)" strokeWidth="1.5" strokeLinecap="round">
          <path d="M12 3L2 21h20L12 3z" />
          <circle cx="12" cy="14" r="2" fill="var(--text-faint, #A09890)" stroke="none" />
        </svg>
      </div>
    );
  }

  const response = sigData.responses[0];
  const config = sigData.configSnapshot;
  const itemConfig = config?.items?.find(itm => itm.id === response.itemId) || config?.items?.[0];
  const poles = itemConfig?.poles || [];
  const prompt = itemConfig?.prompt || '';

  // Mode label + color for badge
  const modeMeta: Record<string, { color: string; label: string }> = {
    tension: { color: '#7BBFAF', label: 'Tension' },
    triad:   { color: '#6E7A5A', label: 'Triad' },
    stones:  { color: '#C2AC8E', label: 'Stones' },
  };
  const meta = modeMeta[response.mode] || { color: '#6E7A5A', label: 'Signification' };

  return (
    <div
      className="w-full relative overflow-hidden flex flex-col items-center justify-center"
      style={{
        background: 'var(--app-bg, #F5F2EE)',
        minHeight: compact ? 120 : 180,
        padding: compact ? '12px 16px' : '20px 24px',
      }}
    >
      {/* Prompt */}
      {!compact && prompt && (
        <p
          className="text-[11px] font-semibold text-center mb-3 leading-snug"
          style={{ color: 'var(--text-ink, #1A1814)', maxWidth: 220 }}
        >
          {prompt.length > 60 ? prompt.slice(0, 60) + '...' : prompt}
        </p>
      )}

      {/* Visualization */}
      {response.mode === 'tension' && (
        <TensionPreview response={response as TensionResponse} poles={poles} />
      )}
      {response.mode === 'triad' && (
        <TriadPreview response={response as TriadResponse} poles={poles} />
      )}
      {response.mode === 'stones' && (
        <StonesPreview response={response as StonesResponse} poles={poles} />
      )}

      {/* Narrative snippet */}
      {!compact && response.narrative && (
        <p
          className="text-[10px] italic text-center mt-2 leading-snug"
          style={{ color: 'var(--text-muted, #6B6560)', maxWidth: 200 }}
        >
          &ldquo;{response.narrative.length > 80 ? response.narrative.slice(0, 80) + '...' : response.narrative}&rdquo;
        </p>
      )}

      {/* Multi-response indicator */}
      {sigData.responses.length > 1 && (
        <span
          className="absolute bottom-2 right-2 text-[9px] font-bold px-1.5 py-0.5 rounded"
          style={{ background: 'rgba(26, 24, 20, 0.06)', color: 'var(--text-faint, #A09890)' }}
        >
          +{sigData.responses.length - 1} more
        </span>
      )}

      {/* Type badge */}
      <span
        className="absolute top-2 left-2 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-white"
        style={{ background: meta.color }}
      >
        {meta.label}
      </span>
    </div>
  );
};
